import dayjs from 'dayjs';
import { message } from 'antd';
import type { FormInstance } from 'antd';
import {
    addBillingPaymentMethod,
    getBillingOnboarding,
    getBillingPaymentMethods,
    saveBillingAddress,
    saveBillingClaimScope,
    saveBillingEra,
    saveBillingProvider,
    saveBillingRenderingProviders,
    saveBillingServiceLocation,
    saveBillingTaxInfo,
} from 'api/insuranceBilling';
import type {
    BillingPaymentMethodVo,
    BillingRenderingProviderDto,
    InsuranceBillingOnboardingVo,
} from 'types/insuranceBilling';
import type { BillingStepKey } from './steps';
import { paymentBridge, squareBrandLabel } from './paymentBridge';
import { npiUnverified } from './utils';

type Values = Record<string, any>;

// ---------- 表单值 ↔ DTO ----------
// 表单按步骤命名空间嵌套（billing / tax / address / location / rendering / ehr / clearinghouse）。
// step key 即后端 onboardingStep slug；此表定义每步保存时要读取的表单命名空间
export const STEP_FIELDS: Record<BillingStepKey, string[]> = {
    'billing-provider': ['billing'],
    'tax-info': ['tax'],
    address: ['address'],
    'service-location': ['location'],
    // rendering 保存需读 billing 命名空间：首条「Same as billing」行的身份字段从 billing echo
    'rendering-providers': ['rendering', 'billing'],
    'claim-scope': ['ehr'],
    era: ['clearinghouse'],
    'payment-method': ['payment'],
};

const digits = (v: any) => String(v ?? '').replace(/\D/g, '');

// Save and exit / stepper 跳步的中途保存不经过 Continue 拦截，表单里可能带着未通过
// Registry 校验的 NPI（不足 10 位 / 查询中 / 查不到）：草稿不落非法值，按未填口径传空，
// 依附于 NPI 的 taxonomy 一并剥离
const verifiedNpiFields = (slot: Values) => {
    const ok = !npiUnverified(slot);
    return { npi: ok ? slot?.npi : undefined, taxonomy: ok ? slot?.taxonomy : undefined };
};

// dayjs / 字符串日期 → UTC ISO-8601（取本地日零点；跨时区改设备可能 ±1 天，可接受）
const toUtcIso = (v: any): string | undefined => {
    if (!v) return undefined;
    const d = dayjs.isDayjs(v) ? v : dayjs(v);
    return d.isValid() ? d.startOf('day').toISOString() : undefined;
};

// provider 表单值 → 接口 DTO。整表替换语义：已有条目必须带 id 回传（缺席即删），
// 首条「Same as billing」行的 firstName/lastName 从 billing 步骤 echo（item 必填），
// NPI/taxonomy 取本行自己的值（billing 为 Organization 时 group NPI 不能当 rendering NPI，
// 行内 NPI 由用户另填；billing 为 Individual 时行内值来自预填，允许用户改过），
// id/bpProviderId/ptan 仍取行内值，否则 owner 条目会被删建；
// npiType/emailStatus（前端辅助字段）不上送；addressChoice/addr 映射为 addressSameAsBilling + 平铺地址
const providerToDto = (p: Values, isFirst: boolean, billing: Values): BillingRenderingProviderDto => {
    const sameAsBilling = isFirst && p?.sameAsBilling === 'yes';
    const dto: BillingRenderingProviderDto = {
        id: p?.id,
        bpProviderId: p?.bpProviderId,
        ptan: p?.ptan,
        taxonomy: p?.taxonomy,
    };
    if (sameAsBilling) {
        dto.firstName = billing?.firstName;
        dto.lastName = billing?.lastName;
        const ownNpi = verifiedNpiFields(p || {});
        dto.npi = ownNpi.npi;
        dto.taxonomy = ownNpi.taxonomy;
    } else {
        dto.firstName = p?.firstName;
        dto.lastName = p?.lastName;
        dto.email = p?.email ? String(p.email).trim() : p?.email;
        const ownNpi = verifiedNpiFields(p || {});
        dto.npi = ownNpi.npi;
        dto.taxonomy = ownNpi.taxonomy;
    }
    // 未选择地址时两者都不带（草稿态；Review 提交前会强制选择）
    if (p?.addressChoice === 'same') {
        dto.addressSameAsBilling = true;
    } else if (p?.addressChoice === 'different') {
        const a = p?.addr || {};
        dto.addressSameAsBilling = false;
        dto.address1 = a.address1;
        dto.address2 = a.address2;
        dto.city = a.city;
        dto.state = a.state;
        dto.postalCode = a.postalCode ? digits(a.postalCode) : a.postalCode;
    }
    return dto;
};

// 提交前出参处理：去命名空间、剥离展示格式（taxId/postalCode 纯数字）、辅助字段不上送
export const stepValuesToDto = (key: BillingStepKey, values: Values): any => {
    if (key === 'billing-provider') {
        const b = values.billing || {};
        return {
            firstName: b.firstName,
            lastName: b.lastName,
            middleName: b.middleName,
            organizationName: b.organizationName,
            ...verifiedNpiFields(b),
        };
    }
    if (key === 'tax-info') {
        const t = values.tax || {};
        return { taxIdType: t.taxIdType, taxId: t.taxId ? digits(t.taxId) : t.taxId };
    }
    if (key === 'address') {
        const a = values.address || {};
        return { ...a, postalCode: a.postalCode ? digits(a.postalCode) : a.postalCode };
    }
    if (key === 'service-location') {
        const l = values.location || {};
        if (l.sameAsBilling !== 'no') {
            return { sameAsBilling: l.sameAsBilling === 'yes' ? true : undefined };
        }
        return {
            sameAsBilling: false,
            facilityName: l.facilityName,
            address1: l.address1,
            address2: l.address2,
            city: l.city,
            state: l.state,
            postalCode: l.postalCode,
        };
    }
    if (key === 'rendering-providers') {
        const providers: Values[] = values.rendering?.providers || [];
        const billing: Values = values.billing || {};
        return {
            firstProviderSameAsBilling: providers[0]?.sameAsBilling === 'yes',
            providers: providers.map((p, i) => providerToDto(p, i === 0, billing)),
        };
    }
    if (key === 'claim-scope') {
        const e = values.ehr || {};
        // otherEhr（「My EHR isn't listed」）走 submitCustomEhr（Tell us about your EHR 弹窗），不在本请求体内
        return {
            billingEhrs: e.billingEhrs || [],
            claimScope: e.claimScope,
            claimStartMode: e.claimStartMode,
            claimStartDate: e.claimStartMode === 'specific' ? toUtcIso(e.claimStartDate) : undefined,
        };
    }
    if (key === 'payment-method') {
        const p = values.payment || {};
        // mock 阶段原样透传（savePaymentMethodStep 自己消化：手动态 tokenize、synced 态直接过）；
        // 真实接口就绪后这里换成 { sourceId }
        return { useManualCard: !!p.useManualCard, cardOnFile: p.cardOnFile };
    }
    // era：switch → yes / keep → no
    const choice = values.clearinghouse?.choice;
    let eraStatus: 'yes' | 'no' | undefined;
    if (choice === 'switch') eraStatus = 'yes';
    if (choice === 'keep') eraStatus = 'no';
    return { eraStatus };
};

// ---------- Payment（Step 8）：save / hydrate ----------
// Step 8 保存：有卡且未开手动录卡时不发请求（卡已在服务端）；手动录卡先经 Square tokenize
// 拿一次性 sourceId 再存卡（换卡 setAsDefault=true 顶替旧默认卡，无需删卡）。
// sourceId 失败不能复用重试——每次保存都重新 tokenize，天然满足。
// 与其它步骤同签名（dto → { error? }），注册进 STEP_API 后三个宿主（向导 Continue /
// Review 内联编辑 / Settings 弹窗）走通用保存路径，无需特判
const savePaymentMethodStep = async (dto: {
    useManualCard?: boolean;
    cardOnFile?: { brand?: string; last4?: string };
}): Promise<{ error?: boolean }> => {
    if (!dto?.useManualCard) {
        if (dto?.cardOnFile?.last4) return {};
        message.error('Please add a payment method.');
        return { error: true };
    }
    if (!paymentBridge.tokenize) {
        message.error('Payment form is not ready yet.');
        return { error: true };
    }
    // tokenize 失败的具体原因已由 SquareCardForm 内联展示
    const result = await paymentBridge.tokenize();
    if (!result) return { error: true };
    const res: any = await addBillingPaymentMethod({ sourceId: result.token, setAsDefault: true });
    // 失败文案由全局 toast 展示；保持手动录卡态供用户重试（会重新 tokenize）
    if (res?.error) return { error: true };
    paymentBridge.onSaved?.(result.card);
    return {};
};

// 拉已存卡回填 payment.cardOnFile（isDefault 那张即会被扣管理费的卡；空列表是正常态 =
// 还没走过 Step 8，StepPayment 会自动进手动录卡态）。Review / Settings 只读视图在
// StepPayment 挂载前就要展示卡号，所以由宿主 hydrate 时调用，不放组件里
export const hydratePaymentMethod = async (form: FormInstance): Promise<void> => {
    try {
        const res: any = await getBillingPaymentMethods();
        if (res?.error) return;
        const list: BillingPaymentMethodVo[] = res?.data?.data || [];
        const def = list.find((m) => m.isDefault) || list[0];
        if (def?.last4) {
            form.setFieldValue(['payment', 'cardOnFile'], { brand: squareBrandLabel(def.brand), last4: def.last4 });
        }
    } catch (e) {
        // 静默：按无卡口径处理（owner-only 接口被拒同理），不阻塞 onboarding
    }
};

// 每步保存接口（key = 后端 onboardingStep slug）
export const STEP_API: Record<BillingStepKey, { save: (dto: any) => Promise<any> }> = {
    'billing-provider': { save: saveBillingProvider },
    'tax-info': { save: saveBillingTaxInfo },
    address: { save: saveBillingAddress },
    'service-location': { save: saveBillingServiceLocation },
    'rendering-providers': { save: saveBillingRenderingProviders },
    'claim-scope': { save: saveBillingClaimScope },
    era: { save: saveBillingEra },
    'payment-method': { save: savePaymentMethodStep },
};

// ---------- hydrate：接口聚合返回 → 命名空间表单值 ----------
const formatTaxIdForDisplay = (taxId?: string, taxIdType?: string) => {
    const d = digits(taxId);
    if (!d) return taxId;
    if (taxIdType === 'ssn') return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2)}`;
};

const hasAny = (obj: Values) => Object.values(obj).some((v) => v !== undefined && v !== null && v !== '');

const providerFromDto = (
    dto: BillingRenderingProviderDto,
    firstProviderSameAsBilling?: boolean | null,
    index = 0,
): Values => {
    const p: Values = {
        id: dto.id,
        bpProviderId: dto.bpProviderId,
        firstName: dto.firstName,
        lastName: dto.lastName,
        email: dto.email,
        npi: dto.npi,
        npiType: dto.npiType || undefined,
        ptan: dto.ptan,
        taxonomy: dto.taxonomy,
    };
    // addressSameAsBilling 仅决定 radio 选中（true 时后端已把地址 copy 成 billing 地址）；
    // 独立地址回填到 addr 命名空间。null/缺省（历史数据）不选中，由 Review 强制补选
    if (dto.addressSameAsBilling === true) {
        p.addressChoice = 'same';
    } else if (dto.addressSameAsBilling === false) {
        p.addressChoice = 'different';
        p.addr = {
            address1: dto.address1,
            address2: dto.address2,
            city: dto.city,
            state: dto.state,
            postalCode: dto.postalCode,
        };
    }
    if (index === 0 && firstProviderSameAsBilling != null) {
        p.sameAsBilling = firstProviderSameAsBilling ? 'yes' : 'no';
    }
    return p;
};

// 后端 billingProvider 为 Step 1-3 扁平合并，这里拆回 billing / tax / address 三个命名空间；
// claimSettings 拆回 ehr / clearinghouse
export const onboardingDtoToFormValues = (vo?: InsuranceBillingOnboardingVo | null): Values => {
    if (!vo) return {};
    const values: Values = {};
    const bp = vo.billingProvider;
    if (bp) {
        const billing = {
            firstName: bp.firstName ?? undefined,
            lastName: bp.lastName ?? undefined,
            middleName: bp.middleName ?? undefined,
            organizationName: bp.organizationName ?? undefined,
            npi: bp.npi ?? undefined,
            taxonomy: bp.taxonomy ?? undefined,
            npiType: bp.npiType || undefined,
        };
        if (hasAny(billing)) values.billing = billing;
        // 后端 GET 实测回传大写（"EIN"），与文档小写枚举不一致；归一小写，保证表单/校验/重存口径一致
        const taxIdType = bp.taxIdType ? (String(bp.taxIdType).toLowerCase() as typeof bp.taxIdType) : undefined;
        const tax = {
            taxIdType,
            taxId: formatTaxIdForDisplay(bp.taxId ?? undefined, taxIdType),
        };
        if (hasAny(tax)) values.tax = tax;
        const address = {
            address1: bp.address1 ?? undefined,
            address2: bp.address2 ?? undefined,
            city: bp.city ?? undefined,
            state: bp.state ?? undefined,
            postalCode: bp.postalCode ?? undefined,
            // UsPhoneInput 只认纯数字；后端旧数据可能带格式，回填时剥掉
            phone: bp.phone ? digits(bp.phone) : undefined,
            fax: bp.fax ? digits(bp.fax) : undefined,
            contactName: bp.contactName ?? undefined,
            contactEmail: bp.contactEmail ?? undefined,
        };
        if (hasAny(address)) values.address = address;
    }
    if (vo.serviceLocation) {
        const l = vo.serviceLocation;
        let sameAsBilling: 'yes' | 'no' | undefined;
        if (l.sameAsBilling != null) sameAsBilling = l.sameAsBilling ? 'yes' : 'no';
        values.location = {
            sameAsBilling,
            facilityName: l.facilityName,
            address1: l.address1,
            address2: l.address2,
            city: l.city,
            state: l.state,
            postalCode: l.postalCode,
        };
    }
    if (vo.renderingProviders?.length) {
        values.rendering = {
            providers: vo.renderingProviders.map((p, i) =>
                providerFromDto(p, vo.claimSettings?.firstProviderSameAsBilling, i),
            ),
        };
    }
    if (vo.claimSettings) {
        const c = vo.claimSettings;
        values.ehr = {
            billingEhrs: c.billingEhrs || [],
            claimScope: c.claimScope || undefined,
            claimStartMode: c.claimStartMode || undefined,
            // claimStartDate 只承载 specific 的用户选择（DatePicker 值）；today 模式后端也回一个固定起始日，
            // 放进只读辅助字段 claimTodayStartDate（原样 UTC 串），避免切到 specific 时被预填、切回 today 时串值
            claimStartDate: c.claimStartMode === 'specific' && c.claimStartDate ? dayjs(c.claimStartDate) : undefined,
            claimTodayStartDate: c.claimStartMode === 'today' ? c.claimStartDate || undefined : undefined,
        };
        if (c.eraStatus) {
            values.clearinghouse = { choice: c.eraStatus === 'yes' ? 'switch' : 'keep' };
        }
    }
    return values;
};

// 拉取聚合数据（best-effort：接口不通返回 null，不阻塞流程）
export const fetchOnboardingVo = async (): Promise<InsuranceBillingOnboardingVo | null> => {
    try {
        const res: any = await getBillingOnboarding();
        if (res?.error) return null;
        return res?.data?.data ?? null;
    } catch (e) {
        return null;
    }
};
